import React, { useState } from 'react';

const AtlassianSupportForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    atlassianSiteUrl: '',
    products: [],
    priority: '',
    subject: '',
    description: '',
    stepsToReproduce: '',
    expectedBehavior: '',
    actualBehavior: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [showToast, setShowToast] = useState(false);

  const productOptions = [
    { id: 'sprint-reporter-jira', label: 'Sprint Reporter (Jira App)' },
    { id: 'sprint-reporter-confluence', label: 'Sprint Reporter Companion (Confluence App)' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low - General question or minor issue' },
    { value: 'medium', label: 'Medium - Issue affecting some functionality' },
    { value: 'high', label: 'High - Major functionality not working' },
    { value: 'critical', label: 'Critical - App completely unusable' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProductChange = (productId) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.includes(productId)
        ? prev.products.filter(id => id !== productId)
        : [...prev.products, productId]
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const selectedProductLabels = formData.products.map(id => 
        productOptions.find(p => p.id === id)?.label
      ).join(', ');

      const formDataToSend = new FormData();
      formDataToSend.append("access_key", "e26f217a-713f-403e-94be-7f81b8f1e932");
      formDataToSend.append("name", `${formData.firstName} ${formData.lastName}`);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("subject", `[Atlassian Support] ${formData.subject}`);
      formDataToSend.append("from_name", "ZYKAIN Atlassian Support");
      
      const message = `
Support Request Details:
========================

Contact Information:
- Name: ${formData.firstName} ${formData.lastName}
- Email: ${formData.email}
- Company: ${formData.company}
- Atlassian Site: ${formData.atlassianSiteUrl}

Product(s) Affected:
${selectedProductLabels}

Priority: ${priorityOptions.find(p => p.value === formData.priority)?.label}

Issue Description:
${formData.description}

Steps to Reproduce:
${formData.stepsToReproduce}

Expected Behavior:
${formData.expectedBehavior}

Actual Behavior:
${formData.actualBehavior}
      `;

      formDataToSend.append("message", message);

      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formDataToSend,
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus("success");
        setShowToast(true);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          company: '',
          atlassianSiteUrl: '',
          products: [],
          priority: '',
          subject: '',
          description: '',
          stepsToReproduce: '',
          expectedBehavior: '',
          actualBehavior: ''
        });

        setTimeout(() => {
          setShowToast(false);
          setSubmitStatus(null);
        }, 5000);
      } else {
        throw new Error(result.message || "Form submission failed");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Oxanium:wght@400;500;600;700;800;900&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800;900&display=swap');

        * {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .logo-text {
          font-family: 'Oxanium', monospace;
          font-weight: 900;
          letter-spacing: 0.02em;
        }

        .content-text {
          font-family: 'Avenir Light', 'Avenir', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-weight: 300;
        }

        .hero-title {
          font-family: 'Poppins', sans-serif;
          font-weight: 600;
          letter-spacing: -0.02em;
          line-height: 1.1;
        }

        a {
          text-decoration: none;
        }

        .grid-pattern {
          background-image:
            linear-gradient(rgba(0, 0, 0, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 0, 0, 0.03) 1px, transparent 1px);
          background-size: 50px 50px;
          mask: linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.2) 5%, rgba(0,0,0,0.8) 10%, rgba(0,0,0,1) 15%, rgba(0,0,0,1) 100%);
          -webkit-mask: linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.2) 5%, rgba(0,0,0,0.8) 10%, rgba(0,0,0,1) 15%, rgba(0,0,0,1) 100%);
        }

        .toast-show {
          opacity: 1;
          transform: translateY(0) scale(1);
          transition: all 0.3s ease-out;
        }

        .btn-hover:hover {
          transform: translateY(-2px) scale(1.05);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
        }
      `}</style>

      {/* Toast Notification */}
      {showToast && submitStatus === "success" && (
        <div className="fixed top-20 left-0 right-0 flex justify-center z-50 px-4">
          <div className="bg-purple-600 text-white px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3 max-w-sm toast-show">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">
                Support request submitted successfully!
              </p>
              <p className="text-xs opacity-90 mt-1">
                We'll get back to you within 24 hours.
              </p>
            </div>
            <button
              onClick={() => {
                setShowToast(false);
                setSubmitStatus(null);
              }}
              className="flex-shrink-0 text-white hover:text-gray-200 transition-colors"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex-shrink-0">
              <a href="/" className="text-2xl logo-text text-gray-800">
                ZYKAIN
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Header Section */}
      <section className="py-20 lg:py-32 bg-gradient-to-b from-gray-50 via-purple-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl hero-title text-gray-900 mb-6">
            Sprint Reporter Support
          </h1>
          <p className="text-lg md:text-xl text-gray-600 content-text max-w-3xl mx-auto leading-relaxed">
            Get help with Sprint Reporter and our other Atlassian apps. Our support team is here to assist you.
          </p>
        </div>
      </section>

      {/* Support Form Section */}
      <section className="py-16 relative overflow-hidden" style={{ backgroundColor: "#faf9fc" }}>
        {/* Grid Pattern with top fade */}
        <div className="absolute inset-0 grid-pattern pointer-events-none"></div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="bg-purple-600 px-6 py-4">
              <h2 className="text-xl font-semibold text-white">Submit a Support Request</h2>
              <p className="text-purple-100 text-sm mt-1">We typically respond within 24 hours</p>
            </div>

            <div className="p-6">
              {submitStatus === "error" && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-sm">
                    ❌ Sorry, there was an error submitting your support request. Please try again or contact us directly at hello@zykain.com.
                  </p>
                </div>
              )}

              <div>
                {/* Contact Information */}
                <div className="mb-8">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2 content-text">
                        First Name *
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        disabled={isSubmitting}
                        className="w-full px-0 py-3 border-0 border-b border-gray-400 focus:border-purple-600 focus:outline-none bg-transparent text-gray-900 placeholder-gray-500 content-text disabled:opacity-50"
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2 content-text">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        disabled={isSubmitting}
                        className="w-full px-0 py-3 border-0 border-b border-gray-400 focus:border-purple-600 focus:outline-none bg-transparent text-gray-900 placeholder-gray-500 content-text disabled:opacity-50"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2 content-text">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        disabled={isSubmitting}
                        className="w-full px-0 py-3 border-0 border-b border-gray-400 focus:border-purple-600 focus:outline-none bg-transparent text-gray-900 placeholder-gray-500 content-text disabled:opacity-50"
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2 content-text">
                        Company/Organization
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        className="w-full px-0 py-3 border-0 border-b border-gray-400 focus:border-purple-600 focus:outline-none bg-transparent text-gray-900 placeholder-gray-500 content-text disabled:opacity-50"
                      />
                    </div>
                  </div>

                  <div className="mt-6">
                    <label htmlFor="atlassianSiteUrl" className="block text-sm font-medium text-gray-700 mb-2 content-text">
                      Atlassian Site URL *
                    </label>
                    <input
                      type="url"
                      id="atlassianSiteUrl"
                      name="atlassianSiteUrl"
                      value={formData.atlassianSiteUrl}
                      onChange={handleInputChange}
                      placeholder="https://yourcompany.atlassian.net"
                      required
                      disabled={isSubmitting}
                      className="w-full px-0 py-3 border-0 border-b border-gray-400 focus:border-purple-600 focus:outline-none bg-transparent text-gray-900 placeholder-gray-500 content-text disabled:opacity-50"
                    />
                  </div>
                </div>

                {/* Product Selection */}
                <div className="mb-8">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Affected Products *</h3>
                  <p className="text-sm text-gray-600 mb-4 content-text">Select all products that are affected by this issue:</p>
                  <div className="space-y-3">
                    {productOptions.map((product) => (
                      <div key={product.id} className="flex items-center">
                        <input
                          type="checkbox"
                          id={product.id}
                          checked={formData.products.includes(product.id)}
                          onChange={() => handleProductChange(product.id)}
                          disabled={isSubmitting}
                          className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 disabled:opacity-50"
                        />
                        <label htmlFor={product.id} className="ml-3 text-sm text-gray-700 content-text">
                          {product.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Priority Level */}
                <div className="mb-8">
                  <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-2 content-text">
                    Priority Level *
                  </label>
                  <select
                    id="priority"
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    className="w-full px-0 py-3 border-0 border-b border-gray-400 focus:border-purple-600 focus:outline-none bg-transparent text-gray-900 content-text disabled:opacity-50"
                  >
                    <option value="">Select priority level...</option>
                    {priorityOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Issue Details */}
                <div className="mb-8">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Issue Details</h3>
                  
                  <div className="mb-6">
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2 content-text">
                      Subject *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      placeholder="Brief description of the issue"
                      required
                      disabled={isSubmitting}
                      className="w-full px-0 py-3 border-0 border-b border-gray-400 focus:border-purple-600 focus:outline-none bg-transparent text-gray-900 placeholder-gray-500 content-text disabled:opacity-50"
                    />
                  </div>

                  <div className="mb-6">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2 content-text">
                      Detailed Description *
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={4}
                      placeholder="Please provide a detailed description of the issue you're experiencing..."
                      required
                      disabled={isSubmitting}
                      className="w-full px-0 py-3 border-0 border-b border-gray-400 focus:border-purple-600 focus:outline-none bg-transparent text-gray-900 placeholder-gray-500 resize-none content-text disabled:opacity-50"
                    ></textarea>
                  </div>

                  <div className="mb-6">
                    <label htmlFor="stepsToReproduce" className="block text-sm font-medium text-gray-700 mb-2 content-text">
                      Steps to Reproduce
                    </label>
                    <textarea
                      id="stepsToReproduce"
                      name="stepsToReproduce"
                      value={formData.stepsToReproduce}
                      onChange={handleInputChange}
                      rows={3}
                      disabled={isSubmitting}
                      className="w-full px-0 py-3 border-0 border-b border-gray-400 focus:border-purple-600 focus:outline-none bg-transparent text-gray-900 placeholder-gray-500 resize-none content-text disabled:opacity-50"
                    ></textarea>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="expectedBehavior" className="block text-sm font-medium text-gray-700 mb-2 content-text">
                        Expected Behavior
                      </label>
                      <textarea
                        id="expectedBehavior"
                        name="expectedBehavior"
                        value={formData.expectedBehavior}
                        onChange={handleInputChange}
                        rows={3}
                        placeholder="What should happen..."
                        disabled={isSubmitting}
                        className="w-full px-0 py-3 border-0 border-b border-gray-400 focus:border-purple-600 focus:outline-none bg-transparent text-gray-900 placeholder-gray-500 resize-none content-text disabled:opacity-50"
                      ></textarea>
                    </div>
                    <div>
                      <label htmlFor="actualBehavior" className="block text-sm font-medium text-gray-700 mb-2 content-text">
                        Actual Behavior
                      </label>
                      <textarea
                        id="actualBehavior"
                        name="actualBehavior"
                        value={formData.actualBehavior}
                        onChange={handleInputChange}
                        rows={3}
                        placeholder="What actually happens..."
                        disabled={isSubmitting}
                        className="w-full px-0 py-3 border-0 border-b border-gray-400 focus:border-purple-600 focus:outline-none bg-transparent text-gray-900 placeholder-gray-500 resize-none content-text disabled:opacity-50"
                      ></textarea>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleFormSubmit}
                    disabled={isSubmitting || formData.products.length === 0}
                    className="bg-purple-600 text-white px-8 py-3 rounded-full hover:bg-purple-700 transition-all shadow-lg font-semibold text-lg btn-hover disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isSubmitting ? "Submitting..." : "Submit Support Request"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Support Options */}
          <div className="mt-12 text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Other Ways to Get Help</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h4 className="font-medium text-gray-900 mb-2">Documentation</h4>
                <p className="text-sm text-gray-600 mb-3 content-text">
                  Check our comprehensive guides and tutorials
                </p>
                <a href="/sprint-reporter-faq" className="text-purple-600 hover:text-purple-700 text-sm font-medium">
                  View FAQ →
                </a>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h4 className="font-medium text-gray-900 mb-2">Email Support</h4>
                <p className="text-sm text-gray-600 mb-3 content-text">
                  Send us an email for general inquiries
                </p>
                <a href="mailto:hello@zykain.com" className="text-purple-600 hover:text-purple-700 text-sm font-medium">
                  hello@zykain.com →
                </a>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h4 className="font-medium text-gray-900 mb-2">Response Time</h4>
                <p className="text-sm text-gray-600 content-text">
                  We typically respond within 24 hours during business days
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="text-white py-12" style={{ backgroundColor: "#16163F" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-4 logo-text">ZYKAIN</h3>
            <p className="text-gray-400 mb-6">Smart Apps, Smarter Solutions</p>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-3">
              <a
                href="/privacy-policy"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Privacy Policy
              </a>
              <a 
                href="/terms-of-service" 
                className="text-gray-400 hover:text-white transition-colors"
              >
                Terms of Service
              </a>
              <a
                href="/sprint-reporter-faq"
                className="text-gray-400 hover:text-white transition-colors"
              >
                FAQ
              </a>
              <a
                href="/security-policy"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Security Policy
              </a>
              <a
                href="/sla-policy"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Service Level Policy
              </a>
              <a
                href="/#contact"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Contact
              </a>
            </div>
            <div className="mt-8 pt-8 border-t border-gray-800">
              <p className="text-gray-500 text-sm">
                © 2025 ZYKAIN. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AtlassianSupportForm;